import { createSignal, onMount, createEffect } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import styles from "../styles";

export default function PinKeyboard(props: {
    onSubmit: (pin: string) => void;
    onPinChange?: (pinLength: number) => void; // New prop to track PIN length changes
    keyboardRef?: any;
    autoFocus?: boolean;
    onBackLeft?: () => void;
    onDown?: () => void; // prop for skip button focus
}) {
    const layout = [
        ['1', '2', '3'],
        ['4', '5', '6'],
        ['7', '8', '9'],
        ['Del', '0', 'Enter']
    ];

    const refs: Array<Array<any>> = layout.map(() => []);
    const [inputValue, setInputValue] = createSignal("");

    // Track the last focused key position
    const [lastFocusedPos, setLastFocusedPos] = createSignal({ row: 0, col: 0 });

    // Watch for PIN changes and notify parent
    createEffect(() => {
        const currentLength = inputValue().length;
        if (props.onPinChange) {
            props.onPinChange(currentLength);
        }

        // Auto-focus to Enter button when 4 characters are entered
        if (currentLength === 4) {
            setTimeout(() => {
                // Focus the Enter button (row 3, col 2)
                refs[3][2]?.setFocus?.();
                setLastFocusedPos({ row: 3, col: 2 });
            }, 100); // Small delay to ensure button is ready
        }
    });

    onMount(() => {
        if (props.autoFocus !== false) {
            refs[0][0]?.setFocus?.();
        }

        if (props.keyboardRef) {
            props.keyboardRef.resetInput = () => {
                setInputValue('');
                // Reset focus to first key when resetting
                setTimeout(() => {
                    refs[0][0]?.setFocus?.();
                    setLastFocusedPos({ row: 0, col: 0 });
                }, 50);
            };

            props.keyboardRef.setFocus = () => {
                // Use last focused position instead of always going to [0][0]
                const pos = lastFocusedPos();
                refs[pos.row]?.[pos.col]?.setFocus?.();
            };

            // New function to focus Enter button
            props.keyboardRef.focusDoneButton = () => {
                if (inputValue().length === 4) {
                    refs[3][2]?.setFocus?.(); // Enter button
                    setLastFocusedPos({ row: 3, col: 2 });
                }
            };
        }
    });

    const handleKeyPress = (key: string) => {
        if (key === 'Del') {
            setInputValue((prev) => prev.slice(0, -1));
        } else if (key === 'Enter') {
            if (inputValue().length === 4) {
                props.onSubmit(inputValue());
            } else {
                // Optionally alert or ignore if pin is not 4 digits
                // alert("PIN must be 4 digits");
            }
        } else {
            if (inputValue().length < 4) {
                setInputValue((prev) => prev + key);
            }
        }
    };

    // Helper function to update last focused position
    const updateLastFocus = (rowIndex: number, colIndex: number) => {
        setLastFocusedPos({ row: rowIndex, col: colIndex });
    };

    return (
        <View>
            <View x={150} y={140}>
                {[0, 1, 2, 3].map((i) => (
                    <View
                        x={i * 100}
                        width={64}
                        height={64}
                        style={{
                            borderRadius: 32,
                            scale: inputValue().length > i ? 1 : 0.85,
                            color: inputValue().length > i ? hexColor("#ffffff") : hexColor("#333333"),
                            border: {
                                width: inputValue().length === i ? 3 : 0,
                                color: inputValue().length > i ? hexColor("#ffffff") : hexColor("#333333"),
                            },
                            transition: {
                                scale: { duration: 150, easing: "ease-out" },
                                color: { duration: 150 },
                            },
                        }}
                    >
                        <Text x={22} y={20} fontSize={48}>
                            {inputValue().length > i ? "*" : ""}
                        </Text>
                    </View>
                ))}
            </View>

            {/* Keyboard */}
            <View x={140} y={250}>
                {layout.map((row, rowIndex) => (
                    <View y={rowIndex * 95}>
                        {row.map((key, colIndex) => (
                            <View
                                ref={(el) => (refs[rowIndex][colIndex] = el)}
                                x={colIndex * 135}
                                forwardStates
                                style={styles.pinKey}
                                onEnter={() => handleKeyPress(key)}
                                onRight={() => {
                                    updateLastFocus(rowIndex, colIndex + 1);
                                    refs[rowIndex][colIndex + 1]?.setFocus?.();
                                }}
                                onLeft={() => {
                                    if (colIndex === 0) {
                                        props.onBackLeft?.();
                                    } else {
                                        updateLastFocus(rowIndex, colIndex - 1);
                                        refs[rowIndex][colIndex - 1]?.setFocus?.();
                                    }
                                }}
                                onDown={() => {
                                    // If we're on the bottom row and onDown prop exists, call it
                                    // But only if PIN is not complete (less than 4 characters)
                                    if (rowIndex === layout.length - 1 && props.onDown && inputValue().length < 4) {
                                        props.onDown();
                                    } else {
                                        updateLastFocus(rowIndex + 1, colIndex);
                                        refs[rowIndex + 1]?.[colIndex]?.setFocus?.();
                                    }
                                }}
                                onUp={() => {
                                    updateLastFocus(rowIndex - 1, colIndex);
                                    refs[rowIndex - 1]?.[colIndex]?.setFocus?.();
                                }}
                                onFocus={() => {
                                    // Update last focused position when any key gets focus
                                    updateLastFocus(rowIndex, colIndex);
                                }}
                            >
                                {key === "Del" ? (
                                    <View
                                        src="https://i.ibb.co/wh6n5N3X/backspace.png"
                                        style={{ width: 40, height: 40, x: 37, y: 20, color: "#ffffff", focus: { color: "#000000" } }}
                                    />
                                ) : key === "Enter" ? (
                                    <View
                                        src="https://i.ibb.co/FbFMtxMw/check.png"
                                        style={{
                                            width: 40,
                                            height: 40,
                                            x: 37,
                                            y: 20,
                                            color: "#ffffff",
                                            focus: { color: "#000000" },
                                            // Highlight Enter button when PIN is complete
                                            ...(inputValue().length === 4 && {
                                                filter: "brightness(1.3)"
                                            })
                                        }}
                                    />
                                ) : (
                                    <Text
                                        fontSize={32}
                                        x={50}
                                        y={25}
                                        style={{ color: "#ffffff", focus: { color: "#000000" } }}
                                    >
                                        {key}
                                    </Text>
                                )}
                            </View>
                        ))}
                    </View>
                ))}
            </View>
        </View>
    );
}